// src/pages/FirmApprovalAssignment/FirmApprovalAssignmentList.js
import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Drawer,
  Popconfirm,
  Tooltip,
  Empty,
  Row,
  Col,
  Tag,
} from "antd";
import {
  OrderedListOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import RequestHandler from "../../util/RequestHandler";
import FirmApprovalAssignmentAddForm from "../Forms/FirmApprovalAssignmentAddForm";
import FirmApprovalAssignmentUpdateForm from "../Forms/FirmApprovalAssignmentUpdateForm";
import { odata, queryHelper } from "../Filters/Helper";
import { useHistory } from "react-router-dom";
import moment from "moment";
import FirmApprovalRoleAssignmentListFilter from "../Filters/FirmApprovalRoleAssignmentListFilter";

// 🎨 Rol -> Renk eşleşmesi
const roleColorMap = {
  "Talep Eden Kullanıcı": "blue",
  "Depo / Stok Sorumlusu": "green",
  "Birim Amiri / Şantiye Şefi": "orange",
  "Satın Alma Birimi": "purple",
  "Mali İşler Müdürü": "magenta",
  "Üst Yönetici (Ek Onay)": "volcano",
  "Depo Teslim Onaycısı": "cyan",
};

const FirmApprovalAssignmentList = (props) => {
  const [addFormVisible, setAddFormVisible] = useState(false);
  const [updateFormVisible, setUpdateFormVisible] = useState(false);
  const [updateData, setUpdateData] = useState(null);
  const [tableData, setTableData] = useState({
    data: [],
    loading: true,
    total: 0,
  });
  const [tableDataQueryOptions, setTableDataQueryOptions] = useState(
    queryHelper.getDefaultTableDataQueryOptions()
  );
  const [firmName, setFirmName] = useState(props.match.params.firmName ?? "");

  useEffect(
    () => getTableData(),
    [props.match.params.firmId, tableDataQueryOptions]
  );

  const history = useHistory();

  const getTableData = () => {
    setTableData({
      ...tableData,
      loading: true,
    });

    let query = "";
    const { skip, take, filter } = tableDataQueryOptions;
    const order = "&$orderby=name asc";
    const conditions = [];
    if (filter) {
      conditions.push(filter);
    }

    if (conditions.length > 0) {
      query = `&$filter=${conditions.join(" and ")}`;
    }
    RequestHandler.get(
      `/FirmApprovalRoleAssignment?firmId=${props.match.params.firmId}&$count=true${query}${order}&$skip=${skip}&$top=${take}`
    ).then((response) => {
      setTableData({
        data: response.value,
        loading: false,
        total: response["@odata.count"],
      });
    });
  };

  const handleDelete = (id) => {
    RequestHandler.delete(`/FirmApprovalRoleAssignment/${id}`).then((res) => {
      if (res.success) getTableData();
    });
  };

  const handleUpdate = (record) => {
    RequestHandler.get(`/FirmApprovalRoleAssignment/GetById/${record.id}`).then(
      (res) => {
        setUpdateData(res);
        setUpdateFormVisible(true);
      }
    );
  };

  const columns = [
    {
      title: "Kullanıcı Adı",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <Tooltip title={text}>
          {text?.length > 40 ? text.substring(0, 40) + "..." : text}
        </Tooltip>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => (
        <Tooltip title={text}>
          {text?.length > 40 ? text.substring(0, 40) + "..." : text}
        </Tooltip>
      ),
    },
    {
      title: "Rol",
      dataIndex: "approvalRoleName",
      key: "approvalRoleName",
      align: "center",
      render: (text) => {
        const color = roleColorMap[text] || "default";
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Ekleyen Kullanıcı",
      dataIndex: "addUserName",
      key: "addUserName",
    },
    {
      title: "Eklenme Tarihi",
      dataIndex: "addDate",
      key: "addDate",
      render: (data) =>
        data ? moment(data).format("DD.MM.YYYY HH:mm:ss") : "-",
    },
    {
      title: "Son Güncelleyen Kullanıcı",
      dataIndex: "updateUserName",
      key: "updateUserName",
    },

    {
      title: "Son Güncellenme Tarihi",
      dataIndex: "updateDate",
      key: "updateDate",
      render: (data) => (data ? moment(data).format("DD.MM.YYYY HH:mm") : "-"),
    },
    {
      title: "Düzenle",
      align: "center",
      render: (record) => (
        <a onClick={() => handleUpdate(record)}>
          <EditOutlined style={{ color: "orange", fontSize: "130%" }} />
        </a>
      ),
    },
    {
      title: "Sil",
      align: "center",
      render: (record) => (
        <Popconfirm
          title="Silmek istiyor musunuz?"
          okText="Evet"
          cancelText="Hayır"
          onConfirm={() => handleDelete(record.id)}
        >
          <a style={{ color: "red", fontSize: "130%" }}>
            <DeleteOutlined />
          </a>
        </Popconfirm>
      ),
    },
  ];

  const onClearTableFilter = () => {
    setTableDataQueryOptions({
      ...tableDataQueryOptions,
      skip: 0,
      current: 1,
      filter: "",
      values: {},
    });
  };

  const onFinishTableFilter = (query, values) => {
    setTableDataQueryOptions((prevState) => {
      return {
        ...prevState,
        skip: 0,
        current: 1,
        filter: query,
        values: values,
      };
    });
  };

  const dataStateChange = (e) => {
    setTableData(e);
  };

  const paging = (p, filters, sorter) => {
    let orderBy = "";
    if (sorter.columnKey && sorter.order) {
      orderBy =
        "&" +
        (sorter.order === "ascend"
          ? odata.orderBy(sorter.columnKey)
          : odata.orderByDesc(sorter.columnKey));
    }
    setTableDataQueryOptions((prevState) => {
      return {
        ...prevState,
        skip: (p.current - 1) * prevState.take,
        take: p.pageSize,
        current: p.current,
      };
    });
  };

  return (
    <>
      <Row style={{ marginBottom: 9 }}>
        <Col flex={1}>
          <a
            onClick={() => history.goBack()}
            style={{ color: "black", marginLeft: "4%" }}
          >
            {" "}
            <RollbackOutlined /> GERİ DÖN
          </a>
        </Col>
        <Col flex={4}></Col>
      </Row>
      <Row>
        <Col xs={24} md={12} lg={16}>
          <p style={{ fontSize: "120%", marginTop: -5 }}>
            <OrderedListOutlined /> PROJE ONAY ROL LİSTESİ
          </p>
        </Col>
        <Col xs={24} md={8} lg={5} style={{ textAlign: "right" }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAddFormVisible(true)}
          >
            Yeni Rol Ekle
          </Button>
        </Col>
        <Col xs={24} md={4} lg={3} style={{ textAlign: "end", fontSize: 15 }}>
          <p style={{ fontWeight: "bold", color: "#2c3e50" }}>
            <span style={{ color: "#6f5196" }}>{firmName}</span>
          </p>
        </Col>
      </Row>

      <FirmApprovalRoleAssignmentListFilter
        data={tableDataQueryOptions.values}
        onClearFilter={onClearTableFilter}
        onFinishFilter={onFinishTableFilter}
        style={{ marginTop: 12, marginBottom: 12 }}
      />

      <Table
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Veri Yok"
            />
          ),
        }}
        style={{
          overflowX: "auto",
          maxHeight: "100%",
          width: "100%",
        }}
        columns={columns}
        onDataStateChange={dataStateChange}
        dataSource={tableData.data}
        loading={tableData.loading}
        pageable={true}
        onChange={paging}
        pagination={{
          defaultPageSize: tableDataQueryOptions.take,
          pageSizeOptions: ["15", "25", "50", "100"],
          total: tableData.total,
          current: tableDataQueryOptions.current,
        }}
        sortable={true}
        rowKey="id"
      />

      {/* Ekleme Formu */}
      <Drawer
        title="Yeni Rol Ekle"
        width={500}
        visible={addFormVisible}
        onClose={() => setAddFormVisible(false)}
        destroyOnClose
      >
        <FirmApprovalAssignmentAddForm
          firmId={props.match.params.firmId}
          onSuccess={() => {
            setAddFormVisible(false);
            getTableData();
          }}
        />
      </Drawer>

      {/* Güncelleme Formu */}
      <Drawer
        title="Rol Güncelle"
        width={500}
        visible={updateFormVisible}
        onClose={() => setUpdateFormVisible(false)}
        destroyOnClose
      >
        <FirmApprovalAssignmentUpdateForm
          data={updateData}
          onSuccess={() => {
            setUpdateFormVisible(false);
            getTableData();
          }}
        />
      </Drawer>
    </>
  );
};

export default FirmApprovalAssignmentList;
